'use client';

import { FiClock } from 'react-icons/fi';
import { HiOutlineChartBar, HiOutlineClock, HiOutlineRocketLaunch } from 'react-icons/hi2';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import { RiMouseLine, RiSparkling2Line, RiTranslate2, RiVoiceprintLine } from 'react-icons/ri';
import RouteChangeScript from '@/components/common/RouteChangeScript';
import widgetConfig, { WidgetConfigPages } from '@/utils/widgetConfigs';
import { HeroBots, IImageOrientation } from '@/components/blocks/HeroBots';
import WhyChooseThisBot, { ColorDefaultChoice } from '@/components/blocks/WhyChooseThisBot';
import ChatbotsNewFeatures from '@/components/blocks/ChatbotsNewFeatures';
import BlockDescriptionImageRight from '@/components/blocks/BlockDescriptionImageRight';
import BlockDescriptionImageLeft from '@/components/blocks/BlockDescriptionImageLeft';
import { CtaTargetType } from '@/components/buttons/LouiseButton';
import ReadyTransformCustomerExperience from '@/components/blocks/ReadyTransformCustomerExperience';
import SectionIntegration from '@/components/sectionIntegration/SectionIntegration';
import React from 'react';
import { SupportedLanguages, useTranslation } from '@/hooks/useTranslation';
import GeneralChatbotBenefits from '@/components/blocks/GeneralChatbotBenefits';
import SiteFrequentlyAskedQuestionsComponent from '@/components/frequently-asked-questions/SiteFrequentlyAskedQuestionsComponent';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';
import { BiSolidPhone } from 'react-icons/bi';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  const sections = t('features/callBot/WhyChooseCallBot/sections', { returnObjects: true }) as Array<any>;

  const hero = {
    content: {
      badge: t('features/callBot/hero-badge'),
      title: t('features/callBot/hero-title'),
      description: t('features/callBot/hero-subtitle'),
      cta: t('features/callBot/hero-cta'),
      trustedBy: t('features/callBot/trusted-by'),
      img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071517080033_callbot_hero.png',
      icon: BiSolidPhone,
      ctaTarget: 'register' as CtaTargetType,
      imgOrientation: 'square' as IImageOrientation,
    },
    benefits: [
      { icon: <AiOutlineThunderbolt className="tw-w-5 tw-h-5" />, text: t('features/callBot/hero-benefit1') },
      { icon: <FiClock className="tw-w-5 tw-h-5" />, text: t('features/callBot/hero-benefit2') },
    ],
    stats: [
      { value: '5 Min', label: t('features/callBot/focus1') },
      { value: '99.9%', label: t('features/callBot/focus2') },
      { value: '100%', label: t('features/callBot/focus3') },
    ],
  };

  const whyChooseThisBot = {
    section: {
      title: t('features/callBot/WhyChooseCallBot/title'),
      subtitle: t('features/callBot/WhyChooseCallBot/subtitle'),
      description: t('features/callBot/WhyChooseCallBot/description'),
    },
    argument: [
      {
        iconicText: t(sections[0].improvement),
        title: t(sections[0].title),
        description: t(sections[0].description),
        outlineIcon: <HiOutlineChartBar className="tw-w-6 tw-h-6 tw-text-purple-600" />,
        metricLabel: t(sections[0].metricLabel),
        metric: t(sections[0].metric),
        colorFrom: 'pink' as ColorDefaultChoice,
        colorTo: 'purple' as ColorDefaultChoice,
      },
      {
        iconicText: t(sections[1].improvement),
        title: t(sections[1].title),
        description: t(sections[1].description),
        outlineIcon: <HiOutlineClock className="tw-w-6 tw-h-6 tw-text-blue-600" />,
        metricLabel: t(sections[1].metricLabel),
        metric: t(sections[1].metric),
        colorFrom: 'blue' as ColorDefaultChoice,
        colorTo: 'cyan' as ColorDefaultChoice,
      },
      {
        iconicText: t(sections[2].improvement),
        title: t(sections[2].title),
        description: t(sections[2].description),
        outlineIcon: <HiOutlineRocketLaunch className="tw-w-6 tw-h-6 tw-text-green-600" />,
        metricLabel: t(sections[2].metricLabel),
        metric: t(sections[2].metric),
        colorFrom: 'green' as ColorDefaultChoice,
        colorTo: 'emerald' as ColorDefaultChoice,
      },
    ],
  };

  const footerSectionBlock = t('features/callBot/NewFeaturesForCallBots/footerSectionBlock', {
    returnObjects: true,
  }) as Array<any>;

  const newFeatures = {
    section: {
      badge: t('features/callBot/NewFeaturesForCallBots/badge'),
      title: t('features/callBot/NewFeaturesForCallBots/title'),
      description: t('features/callBot/NewFeaturesForCallBots/description'),
      subtitle: t('features/callBot/NewFeaturesForCallBots/subtitle'),
      badgeCatchEye: {
        title: t('features/callBot/NewFeaturesForCallBots/badgeCatchEye/title'),
        description: t('features/callBot/NewFeaturesForCallBots/badgeCatchEye/description'),
      },
      footerSectionBlock: footerSectionBlock,
    },
    features: [
      {
        title: t('features/callBot/NewFeaturesForCallBots/features/feature1/title'),
        description: t('features/callBot/NewFeaturesForCallBots/features/feature1/description'),
        subtitle: t('features/callBot/NewFeaturesForCallBots/features/feature1/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25072009133592_voice_setting.png',
        icon: <RiVoiceprintLine className="tw-w-6 tw-h-6 tw-text-white" />,
      },
      {
        title: t('features/callBot/NewFeaturesForCallBots/features/feature2/title'),
        description: t('features/callBot/NewFeaturesForCallBots/features/feature2/description'),
        subtitle: t('features/callBot/NewFeaturesForCallBots/features/feature2/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/2507200913355_easy_to_use.png',
        icon: <RiMouseLine className="tw-w-6 tw-h-6 tw-text-white" />,
      },
      {
        title: t('features/callBot/NewFeaturesForCallBots/features/feature3/title'),
        description: t('features/callBot/NewFeaturesForCallBots/features/feature3/description'),
        subtitle: t('features/callBot/NewFeaturesForCallBots/features/feature3/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25072009133582_multi_lang.png',
        icon: <RiTranslate2 className="tw-w-6 tw-h-6 tw-text-white" />,
      },
    ],
  };

  return (
    <div className="tw-bg-white tw-font-['Lexend'] tw-text-base">
      {/* Dedicated widget */}
      <RouteChangeScript code={widgetConfig.getWidgetCode(WidgetConfigPages.DiscoverCallBot, locale)} />

      {/* Hero Section - Full Width */}
      <HeroBots benefits={hero.benefits} stats={hero.stats} content={hero.content} locale={locale} />

      <AnimatedGradientWrapper>
        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <WhyChooseThisBot
            title={whyChooseThisBot.section.title}
            subtitle={whyChooseThisBot.section.subtitle}
            description={whyChooseThisBot.section.description}
            sections={whyChooseThisBot.argument}
          />

          <ChatbotsNewFeatures
            features={newFeatures.features}
            subtitle={newFeatures.section.subtitle}
            description={newFeatures.section.description}
            title={newFeatures.section.title}
            badge={newFeatures.section.badge}
            badgeCatchEye={newFeatures.section.badgeCatchEye}
            footerSectionBlock={newFeatures.section.footerSectionBlock}
            locale={locale as SupportedLanguages}
          />
        </section>

        {/* Feature Sections */}
        <section className="tw-relative tw-pt-10 lg:tw-pt-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
            <div className="tw-space-y-10">
              <BlockDescriptionImageRight
                badge={t('features/callBot/simple-setup-badge')}
                badgeIcon={<RiSparkling2Line className="tw-w-4 tw-h-4" />}
                title={t('features/callBot/bloc1/title')}
                description={t('features/callBot/bloc1/description')}
                btnColor="blue"
                cta={t('features/callBot/bloc1/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/25071012064476_3steps_2.png'}
                link="register"
                locale={locale}
              />

              <BlockDescriptionImageLeft
                title={t('features/callBot/bloc2/title')}
                description={t('features/callBot/bloc2/description')}
                btnColor="green"
                cta={t('features/callBot/bloc2/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/25072010104588_knowledge.png'}
                link="register"
                locale={locale}
              />

              {/* No Coding */}
              <BlockDescriptionImageRight
                title={t('features/callBot/bloc3/title')}
                description={t('features/callBot/bloc3/description')}
                btnColor="blue"
                cta={t('features/callBot/bloc3/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/25072010104574_staff_free.png'}
                link="register"
                locale={locale}
              />

              <BlockDescriptionImageLeft
                title={t('features/callBot/bloc4/title')}
                description={t('features/callBot/bloc4/description')}
                btnColor="green"
                cta={t('features/callBot/bloc4/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/25072010104667_24-7_business.png'}
                link="register"
                locale={locale}
              />
            </div>
          </div>
        </section>

        <GeneralChatbotBenefits locale={locale} />

        {/* Final CTA - With background but content in container */}
        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <ReadyTransformCustomerExperience locale={locale} />
        </section>

        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <SectionIntegration locale={locale} />
        </section>

        <SiteFrequentlyAskedQuestionsComponent locale={locale} session="CallBotFAQs" />
      </AnimatedGradientWrapper>
    </div>
  );
}
